import { useState } from "react";
import { formatDateOnly, parseDateOnly } from "@/lib/date-only";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { apiGet } from "@/lib/api";
import { useUserFilter, userFilterSearch } from "@/lib/user-filter";
import { UserFilterChip } from "@/components/admin/user-filter-chip";
import { cn } from "@/lib/utils";
import {
  ListChecks,
  AlertCircle,
  Clock,
  CheckCircle,
  ArrowRight,
} from "lucide-react";

export const Route = createFileRoute(
  "/_authenticated/admin/corrective-actions"
)({
  component: AdminCorrectiveActionsPage,
  validateSearch: userFilterSearch,
});

interface ActionSummary {
  id: string;
  title: string;
  description: string | null;
  priority: string;
  status: string;
  dueDate: string | null;
  sourceType: string | null;
  assignedToUserId: string | null;
  assignedToName: string | null;
  createdAt: string;
}

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "overdue", label: "Overdue" },
  { value: "closed", label: "Closed" },
] as const;

const PRIORITY_COLORS: Record<string, string> = {
  low: "bg-blue-100 text-blue-800",
  medium: "bg-yellow-100 text-yellow-800",
  high: "bg-orange-100 text-orange-800",
  critical: "bg-red-100 text-red-800",
};

const STATUS_ICONS: Record<string, typeof AlertCircle> = {
  open: AlertCircle,
  in_progress: Clock,
  overdue: AlertCircle,
  closed: CheckCircle,
};

function AdminCorrectiveActionsPage() {
  const [statusFilter, setStatusFilter] = useState("all");
  const userFilter = useUserFilter();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-corrective-actions", statusFilter, userFilter.userId],
    queryFn: () =>
      apiGet<{ data: ActionSummary[]; total: number }>(
        `/api/corrective-actions?status=${statusFilter}&limit=100${userFilter.userId ? `&assignedToUserId=${encodeURIComponent(userFilter.userId)}` : ""}`
      ),
  });

  const actions = data?.data || [];

  // Summary counts
  const openCount = actions.filter((a) => a.status === "open").length;
  const inProgressCount = actions.filter((a) => a.status === "in_progress").length;
  const overdueCount = actions.filter(
    (a) => a.dueDate && parseDateOnly(a.dueDate) < new Date() && a.status !== "closed"
  ).length;

  return (
    <div className="space-y-4">
      {userFilter.userId && <UserFilterChip name={userFilter.userName} onClear={userFilter.clear} />}
      <div>
        <h2 className="text-xl font-semibold text-text-primary">Corrective Actions</h2>
        <p className="text-sm text-muted-foreground mt-0.5">Track and resolve open safety corrective actions.</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="py-3 px-4 text-center">
            <p className="text-2xl font-bold text-orange-600">{openCount}</p>
            <p className="text-xs text-muted-foreground">Open</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3 px-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{inProgressCount}</p>
            <p className="text-xs text-muted-foreground">In Progress</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3 px-4 text-center">
            <p className="text-2xl font-bold text-red-600">{overdueCount}</p>
            <p className="text-xs text-muted-foreground">Overdue</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
        {STATUS_FILTERS.map((filter) => (
          <button
            key={filter.value}
            type="button"
            onClick={() => setStatusFilter(filter.value)}
            className={cn(
              "inline-flex items-center whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors shrink-0",
              statusFilter === filter.value
                ? "bg-paris-red text-white"
                : "bg-card text-text-secondary border border-border hover:bg-muted/50 hover:text-text-primary"
            )}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-xl" />
          ))}
        </div>
      )}

      {!isLoading && actions.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <ListChecks className="h-12 w-12 text-muted-foreground/40 mb-3" />
            <p className="text-lg font-medium text-muted-foreground">
              No corrective actions
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading && actions.length > 0 && (
        <div className="space-y-2">
          {actions.map((action) => {
            const StatusIcon = STATUS_ICONS[action.status] || AlertCircle;
            const isOverdue =
              action.dueDate &&
              parseDateOnly(action.dueDate) < new Date() &&
              action.status !== "closed";
            return (
              <Link
                key={action.id}
                to="/corrective-actions/$actionId"
                params={{ actionId: action.id }}
              >
                <Card
                  className={cn(
                    "hover:bg-muted/30 transition-colors cursor-pointer",
                    isOverdue && "border-red-200"
                  )}
                >
                  <CardContent className="py-3 px-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">
                          {action.title}
                        </p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge
                            className={cn(
                              "text-xs px-1.5 py-0",
                              PRIORITY_COLORS[action.priority]
                            )}
                          >
                            {action.priority}
                          </Badge>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <StatusIcon className="h-3 w-3" />
                            {action.status.replace("_", " ")}
                          </span>
                          {action.sourceType && (
                            <Badge variant="secondary" className="text-xs px-1.5 py-0">
                              {action.sourceType}
                            </Badge>
                          )}
                          {isOverdue && (
                            <Badge className="bg-red-100 text-red-800 text-xs px-1.5 py-0">
                              Overdue
                            </Badge>
                          )}
                        </div>
                        {action.assignedToName && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Assigned: {action.assignedToName}
                          </p>
                        )}
                        {action.dueDate && (
                          <p className="text-xs text-muted-foreground">
                            Due: {formatDateOnly(action.dueDate)}
                          </p>
                        )}
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
